import { type FC, type FormEvent, useCallback, useEffect } from "react";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { Button } from "@/components/flexkit/Button";
import { LinuxLogo } from "@/assets/logos/LinuxLogo";
import { RunnerRequirementsTable } from "@/components/runners/RunnerRequirementsTable";
import { Text } from "@/components/podkit/typography/Text";
import { useRunnerName } from "@/hooks/use-runner-name";

export const NewLinuxRunnerForm: FC<
    {
        formId: string;
        onSubmit: (values: { name: string }) => Promise<void>;
        onDisabledChange: (disabled: boolean) => void;
        showRequirements?: boolean;
    } & PropsWithClassName
> = ({ formId, onDisabledChange, className, onSubmit, showRequirements = true }) => {
    const { name, setName, isLoading } = useRunnerName({
        baseName: "Linux",
        provider: RunnerProvider.LINUX_HOST,
    });

    // Update disabled state when name changes
    useEffect(() => {
        onDisabledChange(!name);
    }, [name, onDisabledChange]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!name) {
                return;
            }
            await onSubmit({ name });
        },
        [name, onSubmit],
    );

    if (isLoading) {
        return <></>;
    }

    return (
        <form name={formId} id={formId} onSubmit={handleSubmit}>
            <div className={cn("flex flex-col gap-4", className)}>
                {/* Header Section */}
                <div className="flex items-start gap-4 rounded-lg bg-surface-secondary p-4">
                    <div className="flex-shrink-0">
                        <LinuxLogo className="size-12" />
                    </div>
                    <div className="flex grow flex-col gap-3">
                        <div>
                            <Text className="text-base font-semibold">Linux Runner</Text>
                            <Text className="text-sm text-content-secondary">
                                Run environments directly on your Linux machine with full control over your
                                infrastructure.
                            </Text>
                        </div>
                        <div className="space-y-2">
                            <Text className="text-sm">Best suited for:</Text>
                            <ul className="list-disc space-y-1 pl-4 text-sm text-content-secondary">
                                <li>Small teams (2-10 developers) sharing a runner</li>
                                <li>Teams needing local network service access</li>
                                <li>Fixed infrastructure costs (hardware only)</li>
                                <li>Quick evaluation without cloud setup</li>
                            </ul>
                            <Text className="mt-2 text-sm text-content-secondary">
                                Note: Limited to 10 concurrent environments per runner. No horizontal scaling. For
                                production use, consider the AWS runner.
                            </Text>
                        </div>
                        <div>
                            <Button variant="secondary" size="sm" asChild>
                                <ExternalLink href="https://www.gitpod.io/docs/flex/runners/linux" iconSize="sm">
                                    <span>View Documentation</span>
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Runner Name Input */}
                <div className="space-y-1">
                    <Label htmlFor="name">Name</Label>
                    <Input
                        id="name"
                        type="text"
                        name="runner_name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="max-w-full"
                        placeholder="e.g., development-runner"
                    />
                </div>

                {/* Requirements Section */}
                {showRequirements && <RunnerRequirementsTable />}
            </div>
        </form>
    );
};
